import React, { useEffect, useState, useMemo } from 'react';
import { useTranslate } from '../components/TranslationText';
import { useSelector } from 'react-redux';
import axios from "../axios/axiosUmbraco";
import Loader from '../components/Loader';
import ActionButton from '../components/ActionButton';
import FilterButton from '../components/FilterButton';
import Lightbox from 'react-18-image-lightbox';
import EducationalFilter from '../components/EducationalFilter';
import ComingSoon from '../components/ComingSoon';
import { showFirst12Words, showFirst22Words } from '../helper/showFirstSixWords';
import { filterResponsive, pageTitle, rightResponsive, whiteCard } from '../common/cssclasses';
import { getFileName } from '../helper/getFileName';
import { translateMonthToArabic } from '../helper/translateDayToArabic';
import useUmbracoQuery from '../hooks/useUmbracoQuery';
import { formatUmbracoDate } from '../helper/formatUmbracoDate';

const fetchId = process.env.REACT_APP_UMBRACO_EDUCATIONAL_PAGE_ID;
const MediaBaseURL = process.env.REACT_APP_UMBRACO_MEDIA_BASE_URL;

export default function EducationalPage() {
    const { translate } = useTranslate();
    const { currentLanguage } = useSelector((state) => state.studBookSlice);

    const itemsPerPage = 12;
    const [showFilter, setShowFilter] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [index, setIndex] = useState(-1);
    const [categoriesList, setCategoriesList] = useState(['all']);
    const [apiFilters, setApiFilters] = useState({});

    function getCurrentLanguageUmbraco() {
        const lang = localStorage.getItem("eahsLang") ?? "en";
        return lang === "en" ? 'en-US' : 'ar-AE';
    }

    const queryFetchId = fetchId?.startsWith('children:') ? fetchId : `children:${fetchId}`;

    const {
        data: educationalRaw,
        total,
        isLoading,
        hasMore,
        fetchNextPage
    } = useUmbracoQuery(queryFetchId, apiFilters, itemsPerPage, true, '');

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        async function fetchCategories() {
            try {
                const url = `/umbraco/delivery/api/v1/content?fetch=${queryFetchId}&skip=0&take=2000&fields=properties[category]`;
                const umbraco = await axios.get(url, {
                    headers: {
                        "accept-language": getCurrentLanguageUmbraco(),
                    },
                });

                const allCats = umbraco.data.items.map(i => i.properties?.category).filter(Boolean);
                const uniqueCats = Array.from(new Set(allCats));
                setCategoriesList(uniqueCats);
            } catch (e) {
                console.error("Failed to fetch educational categories", e);
            }
        }
        fetchCategories();
    }, [currentLanguage]);

    function formatDateArray(incomingdate) {
        if (!incomingdate) return '';
        const dateTime = new Date(incomingdate);
        const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
        const date = new Date(dateTime);
        const day = String(date.getDate()).padStart(2, '0');
        const month = months[date.getMonth()];
        const year = String(date.getFullYear());
        return [day, month, year];
    }

    const formattedData = useMemo(() => {
         return educationalRaw.map((item) => ({
            id: item.id,
            Item_CoverImage: item.properties?.image ? item.properties?.image[0]?.url : '',
            Item_Date: item.properties.date ? formatDateArray(item?.properties?.date) : '',
            Item_ShortDescription: item?.properties?.subTitle ?? '',
            Item_Category: item?.properties?.category ?? 'all',
            Item_Title: item?.properties?.title ?? '',
            Item_Date_Normal: item.properties.date ? new Date(item?.properties?.date) : '',
            Item_Video_Link: MediaBaseURL + item?.properties?.videoFile ?? ''
        }));
    }, [educationalRaw]);

    const images = useMemo(() => {
        return formattedData.map((video) => ({
            src: video.Item_Video_Link,
            width: 320,
            isvideo: true,
            height: 174,
            caption: 'video',
        }));
    }, [formattedData]);

    const currentImage = images[index];

    const handleFilter = (filters) => {
        const newApiFilters = {};

        // Flutter API key: title
        newApiFilters['title'] = filters.coursTitle?.trim() || '';

        // Flutter API key: category — empty string = All (no filter)
        newApiFilters['category'] = filters.coursecategory || '';

        // Flutter API keys: dateFrom, dateTo — format: yyyy-MM-dd
        newApiFilters['dateFrom'] = filters.DateFrom ? formatUmbracoDate(filters.DateFrom) : '';
        newApiFilters['dateTo'] = filters.DateTo ? formatUmbracoDate(filters.DateTo) : '';

        setApiFilters(newApiFilters);
    };

    const handleClose = () => setIndex(-1);
    function getVideoFrame(index) {
        if (!images[index]) return null;
        return (<video style={{ position: 'relative', zIndex: 100, }} width="100%" height="80%" controls autoPlay><source src={images[index].src} type="video/mp4" /></video>);
    }

    return (
        <>
            <div className='row rtl'>
                {windowWidth < 768 && (
                    <div className="col-md-4 col-sm-6 col-6 text-center d-flex justify-content-center mb-3 mx-auto">
                        <FilterButton onClick={() => setShowFilter(!showFilter)} />
                    </div>
                )}
                <h2 className={pageTitle}>
                    {translate('web.educational', 'Educational')}
                </h2>
                <div className={filterResponsive}
                    style={{
                        display: windowWidth >= 768 ? 'block' : showFilter ? 'block' : 'none',
                    }}>
                    <EducationalFilter onSubmit={handleFilter} categoriesList={categoriesList} />
                </div>

                <div className={rightResponsive}>
                    {isLoading && formattedData.length === 0 && (
                        <div className="text-center w-100 mt-5">
                            <i className="fas fa-spinner fa-spin fa-3x color-eahsred"></i>
                        </div>
                    )}
                    
                    {!isLoading && formattedData.length === 0 && (
                        <div className="text-center w-100 mt-5 bold color-eahsred">
                            {translate('web.nodataFound', 'No Data Found with Your Filter Parameters')}
                        </div>
                    )}

                    <div className="row rtl">
                        {formattedData.length > 0 && formattedData.map((item, idx) => (
                            <div className="col-xl-4 col-lg-6 col-md-12 col-sm-12 mb-main" key={idx} style={{ position: "relative" }}>
                                {EducationalCard(setIndex, idx, item, getCurrentLanguageUmbraco)}
                            </div>
                        ))}
                        
                        {hasMore && formattedData.length > 0 && (
                            <div className="col-lg-12 col-md-12 text-center my-5">
                                <ActionButton label={translate('web.showMore', 'Show More')} type={'loadmore'} onClick={fetchNextPage} disabled={isLoading} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {!!currentImage && (
                /* @ts-ignore */
                <Lightbox
                    mainSrc={images[index]?.src}
                    onCloseRequest={handleClose}
                    wrapperClassName='ltr'
                    enableZoom={false}
                    imageLoadErrorMessage={getVideoFrame(index)}
                />
            )}
        </>
    );
}

function EducationalCard(setIndex, index, item, getCurrentLanguageUmbraco) {
    return (
        <div className={whiteCard} style={{cursor:'pointer'}} onClick={() => setIndex(index)}>
            <div className="row h-100 ">
                <div className='w-100 position-relative mb-form-group'>
                    <div>
                        <img src={`${MediaBaseURL}${item.Item_CoverImage}`} className="w-100 border-15" alt={getFileName(item?.Item_CoverImage)} />
                    </div>
                    <div className='position-absolute bottom-0 left-0 right-0 top-0 w-100 d-flex justify-content-center align-items-center' style={{ cursor: "pointer", fontSize: 50 }} >
                        <i className="fa-lg fa-regular red fa-circle-play w-unset"></i>
                    </div>
                </div>
                <div className="horseCardTitle mb-1">{showFirst12Words(item.Item_Title)}</div>
                <p className="horseCardDescription fw-bold ">{showFirst22Words(item.Item_ShortDescription)}</p>
                <div className="newsPageNewsDateContainer mt-auto" >
                    <div className="newsPageNewsDay">{item.Item_Date[0]}</div>
                    <div className="newsPageDateSpacer"></div>
                    <div className="newsPageMonthAndYear">
                        <span>{translateMonthToArabic(item.Item_Date[1], getCurrentLanguageUmbraco())}</span>
                        <span>{item.Item_Date[2]}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}


